// Timeline.js — v8 : canvas 2D, hauteur = zoom, vraie frise historique

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { events, formatYear, CATEGORIES } from '../data/events';

const MIN_YEAR  = -3500;
const MAX_YEAR  = new Date().getFullYear();
const TOTAL     = MAX_YEAR - MIN_YEAR;
const BASE_H    = 160; // hauteur de référence
const INIT_VIEW = 700; // années visibles au départ (7 siècles)

function getTickInterval(pxPerYear) {
  if (pxPerYear < 0.12) return { major: 1000, label: 'millénaire' };
  if (pxPerYear < 0.3)  return { major: 500,  label: 'siècle×5' };
  if (pxPerYear < 0.8)  return { major: 200,  label: 'siècle×2' };
  if (pxPerYear < 1.5)  return { major: 100,  label: 'siècle' };
  if (pxPerYear < 4)    return { major: 50,   label: 'demi-siècle' };
  if (pxPerYear < 10)   return { major: 20,   label: 'décennie×2' };
  if (pxPerYear < 25)   return { major: 10,   label: 'décennie' };
  if (pxPerYear < 60)   return { major: 5,    label: 'quinquennat' };
  return                       { major: 1,    label: 'année' };
}

const ERA_LABELS = [
  { year: -3000, label: 'Antiquité' },
  { year: -500,  label: 'Grèce & Rome' },
  { year: 476,   label: 'Moyen Âge' },
  { year: 1000,  label: 'An Mil' },
  { year: 1492,  label: 'Renaissance' },
  { year: 1800,  label: 'Moderne' },
];


// ── Composant année éditable ─────────────────────────────────────────────────

function YearInput({ year, onChange }) {
  const [editing, setEditing] = useState(false);
  const [val,     setVal]     = useState(String(Math.abs(year)));
  const [isBC,    setIsBC]    = useState(year < 0);

  useEffect(() => {
    if (!editing) {
      setVal(String(Math.abs(year)));
      setIsBC(year < 0);
    }
  }, [year, editing]);

  const commit = (v, bc) => {
    const n = parseInt(v);
    if (!isNaN(n) && n >= 0) {
      onChange(bc ? -n : n);
    }
    setEditing(false);
  };

  const step = (delta) => {
    const cur = year;
    onChange(Math.max(-3500, Math.min(new Date().getFullYear(), cur + delta)));
  };

  return (
    <div className="tl-year-input-wrap">
      <button className="tl-year-step" onClick={() => step(-1)} title="-1 an">◂</button>
      <button className="tl-year-step" onClick={() => step(-10)} title="-10 ans">◂◂</button>
      {editing ? (
        <div className="tl-year-edit">
          <input
            className="tl-year-field"
            type="number"
            value={val}
            autoFocus
            onChange={e => setVal(e.target.value)}
            onBlur={() => commit(val, isBC)}
            onKeyDown={e => {
              if (e.key === 'Enter') commit(val, isBC);
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          <button
            className={`tl-bc-toggle ${isBC ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); setIsBC(b => !b); }}
          >
            {isBC ? 'av. J.-C.' : 'ap. J.-C.'}
          </button>
        </div>
      ) : (
        <button
          className="tl-year-display"
          onClick={() => setEditing(true)}
          title="Cliquer pour modifier l'année"
        >
          {Math.abs(year)}{year < 0 ? ' av. J.-C.' : ''}
        </button>
      )}
      <button className="tl-year-step" onClick={() => step(10)} title="+10 ans">▸▸</button>
      <button className="tl-year-step" onClick={() => step(1)} title="+1 an">▸</button>
    </div>
  );
}

export function Timeline({ year, onChange, onEventClick, zoomLevel = BASE_H }) {
  const canvasRef = useRef(null);
  const s         = useRef({
    // zoom initial : 7 siècles visibles
    pxPerYear:   900 / INIT_VIEW,
    viewStart:   800, // centré sur an 1100 (Moyen Âge visible)
    width:       900,
    height:      BASE_H,
    dragging:    false,
    dragX:       0,
    dragVS:      MIN_YEAR,
    hovered:     null,
  });
  const yearRef   = useRef(year);
  const cbChange  = useRef(onChange);
  const cbEvent   = useRef(onEventClick);
  useEffect(() => { cbChange.current = onChange; }, [onChange]);
  useEffect(() => { cbEvent.current = onEventClick; }, [onEventClick]);

  // ── Sync zoom level → pxPerYear ──────────────────────────────────────────
  useEffect(() => {
    const st = s.current;
    const minPpY = st.width / TOTAL;
    const zoom   = Math.max(1, zoomLevel / BASE_H);
    const center = st.viewStart + st.width / st.pxPerYear / 2;
    st.pxPerYear = Math.max(minPpY, minPpY * zoom);
    // Centre la vue sur l'année courante
    st.viewStart = Math.max(MIN_YEAR, Math.min(
      MAX_YEAR - st.width / st.pxPerYear,
      center - st.width / st.pxPerYear / 2
    ));
    draw();
  }, [zoomLevel]); // eslint-disable-line

  // ── Draw ─────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { pxPerYear, viewStart, width, height } = s.current;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = width  * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const cy      = height * 0.58; // ligne un peu en dessous du centre
    const viewEnd = viewStart + width / pxPerYear;
    const xOf     = (y) => (y - viewStart) * pxPerYear;

    // Scale selon hauteur
    const sc       = Math.max(0.8, height / BASE_H);
    const dotR     = 6 * sc;
    const tickH    = 20 * sc;
    const fs       = Math.round(15 * sc);   // labels années
    const fsEra    = Math.round(13 * sc);   // labels époque

    // ── Fond dégradé subtil ──────────────────────────────────────────────
    const grad = ctx.createLinearGradient(0, 0, 0, height);
    grad.addColorStop(0,   'rgba(5,8,20,0)');
    grad.addColorStop(0.4, 'rgba(8,14,35,0.3)');
    grad.addColorStop(1,   'rgba(5,8,20,0.7)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // ── Zones d'époque colorées (très subtiles) ──────────────────────────
    const eraZones = [
      { from: -3500, to: -500,  color: 'rgba(201,162,39,0.04)' },
      { from: -500,  to: 476,   color: 'rgba(68,136,255,0.04)' },
      { from: 476,   to: 1492,  color: 'rgba(120,60,180,0.04)' },
      { from: 1492,  to: 1800,  color: 'rgba(40,160,80,0.04)'  },
      { from: 1800,  to: MAX_YEAR, color: 'rgba(255,100,60,0.04)' },
    ];
    for (const z of eraZones) {
      const x1 = Math.max(0, xOf(z.from));
      const x2 = Math.min(width, xOf(z.to));
      if (x2 > x1) {
        ctx.fillStyle = z.color;
        ctx.fillRect(x1, 0, x2 - x1, height);
      }
    }

    // ── Ligne principale ─────────────────────────────────────────────────
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(80,140,240,0.5)';
    ctx.lineWidth   = 1.5 * sc;
    ctx.moveTo(0, cy);
    ctx.lineTo(width, cy);
    ctx.stroke();

    // ── Ticks + labels ───────────────────────────────────────────────────
    const { major } = getTickInterval(pxPerYear);
    const first = Math.ceil(viewStart / major) * major;
    ctx.font      = `${fs}px 'Space Mono', monospace`;
    ctx.textAlign = 'center';

    for (let y = first; y <= viewEnd; y += major) {
      if (y < MIN_YEAR || y > MAX_YEAR) continue;
      const x     = xOf(y);
      const isJ   = y === 0;
      const isBig = y % (major * 5) === 0;

      // Tick
      ctx.beginPath();
      ctx.strokeStyle = isJ ? 'rgba(255,140,60,0.9)'
        : isBig ? 'rgba(140,180,255,0.6)' : 'rgba(100,140,220,0.35)';
      ctx.lineWidth = isJ ? 2 : isBig ? 1.5 : 0.8;
      const th = isJ ? tickH * 1.4 : isBig ? tickH : tickH * 0.6;
      ctx.moveTo(x, cy - th);
      ctx.lineTo(x, cy + th * 0.4);
      ctx.stroke();

      // Label
      if (isBig || isJ || major <= 10) {
        ctx.fillStyle = isJ ? 'rgba(255,160,80,0.9)'
          : 'rgba(160,200,255,0.7)';
        const lbl = isJ ? 'J.-C.' : y < 0 ? `${Math.abs(y)} av.` : String(y);
        ctx.fillText(lbl, x, cy + th * 0.4 + fs + 2);
      }
    }

    // ── Labels époque ────────────────────────────────────────────────────
    ctx.font      = `bold ${fsEra}px 'Space Mono', monospace`;
    ctx.textAlign = 'left';
    for (const era of ERA_LABELS) {
      const x = xOf(era.year);
      if (x < -60 || x > width) continue;
      ctx.fillStyle = 'rgba(140,180,240,0.55)';
      ctx.fillText(era.label.toUpperCase(), Math.max(2, x + 4), cy - tickH * 1.8 - fsEra);
    }

    // ── Événements ───────────────────────────────────────────────────────
    const curYear = yearRef.current;
    for (const ev of events) {
      if (ev.year < viewStart - 20 || ev.year > viewEnd + 20) continue;
      const x   = xOf(ev.year);
      const cat = CATEGORIES[ev.category];
      const sel = ev.year === curYear;
      const hov = s.current.hovered?.id === ev.id;
      const r   = sel ? dotR * 1.7 : hov ? dotR * 1.4 : dotR;

      // Trait vertical
      ctx.beginPath();
      ctx.strokeStyle = cat.color + (sel ? 'dd' : hov ? 'aa' : '55');
      ctx.lineWidth   = sel ? 2 : 1;
      ctx.moveTo(x, cy);
      ctx.lineTo(x, cy - r - 2);
      ctx.stroke();

      // Point
      ctx.beginPath();
      ctx.arc(x, cy - r - 2, r, 0, Math.PI * 2);
      const grd = ctx.createRadialGradient(x - r*0.3, cy - r - r*0.3, 0, x, cy - r - 2, r);
      grd.addColorStop(0, cat.color + 'ff');
      grd.addColorStop(1, cat.color + 'aa');
      ctx.fillStyle = grd;
      ctx.fill();
      if (sel || hov) {
        ctx.strokeStyle = sel ? 'white' : cat.color;
        ctx.lineWidth   = sel ? 2 : 1.5;
        ctx.stroke();
        // Glow
        ctx.shadowColor = cat.color;
        ctx.shadowBlur  = sel ? 12 : 6;
        ctx.fill();
        ctx.shadowBlur  = 0;
      }

      // Tooltip au survol — fenêtre stylée
      if (hov || sel) {
        const title = ev.title;
        const yearLbl = formatYear(ev.year);
        const tfs = Math.round(12 * sc);
        const yfs = Math.round(10 * sc);
        ctx.font = `bold ${tfs}px 'Space Mono', monospace`;
        const tw  = Math.max(ctx.measureText(title).width, ctx.measureText(yearLbl).width);
        const pad = 10;
        const bw  = tw + pad * 2;
        const bh  = tfs + yfs + pad * 2 + 4;
        const lx  = Math.max(bw/2 + 4, Math.min(width - bw/2 - 4, x));
        const ly  = cy - r - 12 - bh;
        // Ombre
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur  = 8;
        // Fond
        ctx.fillStyle = 'rgba(8,14,35,0.97)';
        ctx.beginPath();
        ctx.roundRect(lx - bw/2, ly, bw, bh, 8);
        ctx.fill();
        ctx.shadowBlur = 0;
        // Bordure couleur catégorie
        ctx.strokeStyle = cat.color;
        ctx.lineWidth   = 2;
        ctx.stroke();
        // Petite flèche
        ctx.beginPath();
        ctx.fillStyle = cat.color;
        ctx.moveTo(x - 6, ly + bh);
        ctx.lineTo(x + 6, ly + bh);
        ctx.lineTo(x, ly + bh + 8);
        ctx.fill();
        // Titre
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${tfs}px 'Space Mono', monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(title.length > 28 ? title.slice(0, 26) + '…' : title, lx, ly + pad + tfs);
        // Année
        ctx.fillStyle = cat.color;
        ctx.font = `${yfs}px 'Space Mono', monospace`;
        ctx.fillText(yearLbl, lx, ly + pad + tfs + 4 + yfs);
      }
    }

    // ── Curseur ──────────────────────────────────────────────────────────
    const cx2 = xOf(curYear);
    if (cx2 >= -2 && cx2 <= width + 2) {
      ctx.beginPath();
      ctx.strokeStyle = '#4499ff';
      ctx.lineWidth   = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.moveTo(cx2, 0);
      ctx.lineTo(cx2, height);
      ctx.stroke();
      ctx.setLineDash([]);

      // Badge année
      const lbl = formatYear(curYear);
      const bfs = Math.round(22 * sc); // TRÈS grand
      ctx.font  = `bold ${bfs}px 'Space Mono', monospace`;
      const tw  = ctx.measureText(lbl).width + 20;
      const bx  = Math.max(tw/2 + 4, Math.min(width - tw/2 - 4, cx2));
      // Fond
      ctx.fillStyle = 'rgba(5,8,20,0.94)';
      ctx.beginPath();
      ctx.roundRect(bx - tw/2, 4, tw, bfs + 10, 6);
      ctx.fill();
      // Bordure
      ctx.strokeStyle = '#4499ff';
      ctx.lineWidth   = 2;
      ctx.stroke();
      // Texte
      ctx.fillStyle   = '#aaccff';
      ctx.textAlign   = 'center';
      ctx.fillText(lbl, bx, bfs + 8);
    }
  }, []);

  // ── Year change → redraw ─────────────────────────────────────────────────
  useEffect(() => {
    yearRef.current = year;
    // Auto-scroll si hors champ
    const st = s.current;
    const cx = (year - st.viewStart) * st.pxPerYear;
    if (cx < 40 || cx > st.width - 40) {
      st.viewStart = Math.max(MIN_YEAR, Math.min(
        MAX_YEAR - st.width / st.pxPerYear,
        year - st.width / st.pxPerYear / 2
      ));
    }
    draw();
  }, [year, draw]);

  // ── ResizeObserver ───────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      const h = e.contentRect.height;
      const st = s.current;
      const minPpY = w / TOTAL;
      if (st.pxPerYear < minPpY) st.pxPerYear = minPpY;
      st.width  = w;
      st.height = h;
      draw();
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [draw]);

  // ── Wheel zoom ───────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e) => {
      e.preventDefault();
      const st   = s.current;
      const rect = canvas.getBoundingClientRect();
      const mx   = e.clientX - rect.left;
      const my   = st.viewStart + mx / st.pxPerYear;
      const f    = e.deltaY < 0 ? 1.2 : 1 / 1.2;
      const minP = st.width / TOTAL;
      st.pxPerYear = Math.max(minP, Math.min(20, st.pxPerYear * f));
      st.viewStart = Math.max(MIN_YEAR, Math.min(
        MAX_YEAR - st.width / st.pxPerYear,
        my - mx / st.pxPerYear
      ));
      draw();
      cbChange.current(Math.round(st.viewStart + st.width / st.pxPerYear / 2));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [draw]);

  // ── Mouse ────────────────────────────────────────────────────────────────
  const hitEvent = (clientX) => {
    const st   = s.current;
    const rect = canvasRef.current.getBoundingClientRect();
    const x    = clientX - rect.left;
    const sc   = Math.max(0.6, st.height / BASE_H);
    const hit  = sc * 5 * 1.8 + 4;
    for (const ev of events) {
      const ex = (ev.year - st.viewStart) * st.pxPerYear;
      if (Math.abs(ex - x) < hit) return ev;
    }
    return null;
  };

  const onMouseDown = (e) => {
    const ev = hitEvent(e.clientX);
    if (ev) return;
    const st = s.current;
    st.dragging = true;
    st.dragX    = e.clientX;
    st.dragVS   = st.viewStart;
  };

  const onMouseMove = (e) => {
    const st  = s.current;
    const ev  = hitEvent(e.clientX);
    const pre = st.hovered;
    st.hovered = ev || null;
    canvasRef.current.style.cursor = ev ? 'pointer' : st.dragging ? 'grabbing' : 'grab';
    if (ev !== pre) draw();
    if (!st.dragging) return;
    const dx = e.clientX - st.dragX;
    st.viewStart = Math.max(MIN_YEAR, Math.min(
      MAX_YEAR - st.width / st.pxPerYear,
      st.dragVS - dx / st.pxPerYear
    ));
    draw();
    cbChange.current(Math.round(st.viewStart + st.width / st.pxPerYear / 2));
  };

  const onMouseUp = (e) => {
    const st = s.current;
    if (!st.dragging) {
      const ev = hitEvent(e.clientX);
      if (ev) {
        cbChange.current(ev.year);
        cbEvent.current?.(ev);
      } else {
        const rect = canvasRef.current.getBoundingClientRect();
        const x    = e.clientX - rect.left;
        cbChange.current(Math.round(st.viewStart + x / st.pxPerYear));
      }
    }
    st.dragging = false;
  };

  const goPrev = () => {
    const st   = s.current;
    const cur  = yearRef.current;
    const prev = [...events].filter(e => e.year < cur).sort((a,b) => b.year - a.year)[0];
    if (!prev) return;
    yearRef.current = prev.year;
    cbChange.current(prev.year);
    cbEvent.current?.(prev);
    st.viewStart = Math.max(MIN_YEAR, Math.min(
      MAX_YEAR - st.width / st.pxPerYear,
      prev.year - st.width / st.pxPerYear / 2
    ));
    draw();
  };

  const goNext = () => {
    const st   = s.current;
    const cur  = yearRef.current;
    const next = [...events].filter(e => e.year > cur).sort((a,b) => a.year - b.year)[0];
    if (!next) return;
    yearRef.current = next.year;
    cbChange.current(next.year);
    cbEvent.current?.(next);
    st.viewStart = Math.max(MIN_YEAR, Math.min(
      MAX_YEAR - st.width / st.pxPerYear,
      next.year - st.width / st.pxPerYear / 2
    ));
    draw();
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', width:'100%', gap:4, userSelect:'none', position:'relative' }}>

      <canvas
        ref={canvasRef}
        style={{ flex:1, width:'100%', minHeight:50, display:'block', cursor:'grab' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => {
          s.current.dragging = false;
          s.current.hovered  = null;
          draw();
        }}
      />

      {/* Boutons navigation centrés */}
      <div className="tl-nav-center">
        <button className="tl-nav-btn" onClick={goPrev}>
          <span className="tl-nav-arrow">◀</span>
          <span>Évén. préc.</span>
        </button>
        <YearInput year={year} onChange={cbChange.current} />
        <button className="tl-nav-btn" onClick={goNext}>
          <span>Évén. suiv.</span>
          <span className="tl-nav-arrow">▶</span>
        </button>
      </div>
    </div>
  );
}
